import ChildCare from '@mui/icons-material/ChildCare';
import LocalOffer from '@mui/icons-material/LocalOffer';
import LocationCity from '@mui/icons-material/LocationCity';
import People from '@mui/icons-material/People';
import PieChart from '@mui/icons-material/PieChart';
import Public from '@mui/icons-material/Public';
import ViewColumn from '@mui/icons-material/ViewColumn';
import VpnKey from '@mui/icons-material/VpnKey';
import Work from '@mui/icons-material/Work';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import Skeleton from '@mui/material/Skeleton';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { CanvasCell, CellRow, ColorIndicator, ConditionalWrapper } from '@qpr/components';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { CanvasCellState } from '../business-model-canvas-state';
import MetricsDetails from './MetricsDetails';

const PREFIX = 'BusinessModelCanvas';

const classes = {
  modal: `${PREFIX}-modal`,
  canvasCell: `${PREFIX}-canvasCell`,
  halfCanvasCell: `${PREFIX}-halfCanvasCell`,
  divider: `${PREFIX}-divider`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(() => ({
  [`& .${classes.modal}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },

  [`& .${classes.canvasCell}`]: {
    height: '480px'
  },

  [`& .${classes.halfCanvasCell}`]: {
    height: '236px'
  },

  [`& .${classes.divider}`]: {
    marginLeft: '8px',
    marginRight: '8px'
  }
}));

// eslint-disable-next-line no-shadow
enum Titles {
  KeyPartners = 'key partners',
  KeyActivities = 'key activities',
  KeyResources = 'key resources',
  ValuePropositions = 'value propositions',
  CustomerRelationships = 'customer relationships',
  Channels = 'channels',
  CustomerSegments = 'customer segments',
  CostStructure = 'cost structure',
  RevenueStreams = 'revenue streams'
}

interface BusinessModelCanvasProps {
  elements: CanvasCellState[];
  inited: boolean;
}

export default function BusinessModelCanvas({ elements = [], inited }: BusinessModelCanvasProps) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [chosenCell, setChosenCell] = React.useState<string>('');

  const colorIndicatorClickHandler = (event: React.MouseEvent<HTMLElement>): void => {
    event.preventDefault();

    setChosenCell(event.currentTarget.parentElement?.id || '');
    setModalOpen(true);
  };

  const modalCloseHandler = (): void => {
    setModalOpen(false);
    setChosenCell('');
  };

  const getCanvasCell = (
    name: string,
    icon: JSX.Element,
    cellClass = classes.canvasCell
  ): JSX.Element | null => {
    const element = elements.find(item => item.name.toLowerCase() === name);

    if (!inited) return <Skeleton variant="rectangular" className={cellClass} />;

    return element ? (
      <CanvasCell id={element.id} title={element.name} className={cellClass} icon={icon}>
        {element.rows.map(row => (
          <ConditionalWrapper
            key={`link${row.id}`}
            condition={element.name === 'Key Activities'}
            wrapper={(children: React.ReactNode) => (
              <RouterLink to={`${row.id}/-/`}>{children}</RouterLink>
            )}
          >
            <CellRow key={row.id}>
              <ColorIndicator color={row.rangecolor || ''} onClick={colorIndicatorClickHandler} />
              <Typography color="inherit" variant="body2">
                {row.name}
              </Typography>
            </CellRow>
            <Divider variant="middle" classes={{ middle: classes.divider }} />
          </ConditionalWrapper>
        ))}
      </CanvasCell>
    ) : null;
  };

  return (
    <Root>
      <Container
        maxWidth="xl"
        component="main"
        id={inited ? 'bmcContainer' : 'skeletonContainer'}
        data-testid={inited ? 'bmc-main' : 'bmc-main-loading'}
      >
        <Grid container spacing={1}>
          <Grid item xs={12} sm={12} md={12}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={12} md>
                {getCanvasCell(Titles.KeyPartners, <People />)}
              </Grid>

              <Grid item xs={12} sm={12} md>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6} md={12}>
                    {getCanvasCell(Titles.KeyActivities, <VpnKey />, classes.halfCanvasCell)}
                  </Grid>
                  <Grid item xs={12} sm={6} md={12}>
                    {getCanvasCell(Titles.KeyResources, <LocationCity />, classes.halfCanvasCell)}
                  </Grid>
                </Grid>
              </Grid>

              <Grid xs={12} item sm={12} md>
                {getCanvasCell(Titles.ValuePropositions, <LocalOffer />)}
              </Grid>

              <Grid item xs={12} sm={12} md>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6} md={12}>
                    {getCanvasCell(
                      Titles.CustomerRelationships,
                      <ChildCare />,
                      classes.halfCanvasCell
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6} md={12}>
                    {getCanvasCell(Titles.Channels, <Public />, classes.halfCanvasCell)}
                  </Grid>
                </Grid>
              </Grid>

              <Grid xs={12} item sm={12} md>
                {getCanvasCell(Titles.CustomerSegments, <ViewColumn />)}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={6}>
                {getCanvasCell(Titles.CostStructure, <PieChart />, classes.halfCanvasCell)}
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                {getCanvasCell(Titles.RevenueStreams, <Work />, classes.halfCanvasCell)}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
      <Modal
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        className={classes.modal}
        open={modalOpen}
        onClose={modalCloseHandler}
      >
        <>
          <MetricsDetails elementId={chosenCell} handleModalClose={modalCloseHandler} />
        </>
      </Modal>
    </Root>
  );
}
